import type {ReactElement} from 'react';
import {useState} from 'react';
import {InputField} from '@Components/input-field-v2';
import {InputControlItemType} from '@Components/input-field-v2/input-field.types';
import {Tooltip} from '@Components/tooltip';
import {Text, TextSize} from '@Components/text';
import {hasAlphaNumericsOnly} from '@Utils/string.util';
import {copyToClipboard} from '@Utils/clipboard.util';
import styles from './picker-input-field.module.scss';
import type {RGBA} from '@Utils/color.util';
import {hexToRgba, rgbaToHex} from '@Utils/color.util';

interface PickerInputFieldProps {
  color: RGBA | string;
  inputContainerClassName?: string;

  onChangeColor(color: RGBA | string | null): void;

  onChangeEnded(color: RGBA | string | null): void;

  isAlphaDisabled?: boolean;

  tooltipClasses?: string;
  isMobileVersion?: boolean;
  inputClassname?: string;
}

export function PickerInputField({
  isAlphaDisabled = false,
  tooltipClasses = '',
  inputContainerClassName = '',
  isMobileVersion = false,
  ...props
}: PickerInputFieldProps): ReactElement<any> {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isColorChanging, setIsColorChanging] = useState(false);
  const isFirstCharacterHash = (value: string): boolean => {
    return value.length > 0 && value.startsWith('#');
  };
  const parseColorForInputField = (value: string): string => {
    if (isFirstCharacterHash(value)) {
      return value.slice(1).toUpperCase();
    }

    return value.toUpperCase();
  };
  const [colorDuringChange, setColorDuringChange] = useState<string>(parseColorForInputField(isAlphaDisabled ? (props.color as string) : rgbaToHex(props.color as RGBA, false)));

  const onClickCopy = async (): Promise<void> => {
    await copyToClipboard(parseColorForInputField(isAlphaDisabled ? (props.color as string) : rgbaToHex(props.color as RGBA, false)).toUpperCase());
  };

  const showToolTip = async (): Promise<void> => {
    setShowTooltip(true);
    await onClickCopy().finally(() => {
      setTimeout(() => {
        setShowTooltip(false);
      }, 1000);
    });
  };

  const isValidInput = (value: string): boolean => {
    if (value.length > 6) {
      return false;
    }
    return hasAlphaNumericsOnly(value);
  };

  const onChangeHandler = (value: string): void => {
    if (!isValidInput(value)) {
      return;
    }
    if (!isColorChanging) {
      setIsColorChanging(true);
    }
    setColorDuringChange(value);
    props.onChangeColor(isAlphaDisabled ? value : hexToRgba(value));
  };

  const onChangeEnded = (value: string): void => {
    if (!isValidInput(value)) {
      return;
    }
    setColorDuringChange(value);
    props.onChangeEnded(isAlphaDisabled ? value : hexToRgba(value));
  };

  const getInputFieldText = (): string => {
    if (isColorChanging) {
      return colorDuringChange;
    }
    return parseColorForInputField(isAlphaDisabled ? (props.color as string) : rgbaToHex(props.color as RGBA, false));
  };

  return (
    <>
      <InputField
        className={inputContainerClassName}
        input={getInputFieldText()}
        onInputChange={onChangeHandler}
        onInputChangeEnded={onChangeEnded}
        onInputBlur={(): void => {
          setIsColorChanging(false);
        }}
        hasIcon="icon-hash"
        elementTitle={window.i18next.t('pmwjs_add_hex_color')}
        controls={
          isMobileVersion
            ? {
                showDefaultControls: false,
                customControls: [
                  {
                    id: 'icon-copy',
                    type: InputControlItemType.ICONS_ONLY,
                    icon: 'icon-copy',
                  },
                ],
                onClick: void showToolTip,
              }
            : {showDefaultControls: false}
        }
        inputClassName={props.inputClassname}
        selectOnFocus
      />
      {showTooltip ? (
        <Tooltip
          className={`${styles.tooltip} ${tooltipClasses}`}
          id="copy-hex-tooltip"
          text={<Text val={window.i18next.t('pmwjs_insights_keywords_copied')} size={TextSize.XXSMALL} />}
        />
      ) : null}
    </>
  );
}
